import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import './MobileGroupDetail.css';

// Editable text component - tap to edit with rainbow saving animation
function EditableText({
  value,
  onSave,
  className = '',
  placeholder = 'Tap to edit',
  multiline = false,
  inputType = 'text'
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value || '');
  const [isSaving, setIsSaving] = useState(false);
  const [showSaved, setShowSaved] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!isEditing && !isSaving) {
      setEditValue(value || '');
    }
  }, [value, isEditing, isSaving]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      const len = inputRef.current.value.length;
      inputRef.current.setSelectionRange(len, len);
    }
  }, [isEditing]);

  const handleTap = useCallback((e) => {
    e.stopPropagation();
    if (!isEditing && !isSaving) {
      setIsEditing(true);
      if (navigator.vibrate) navigator.vibrate(5);
    }
  }, [isEditing, isSaving]);

  const handleSave = useCallback(async () => {
    if (editValue === value) {
      setIsEditing(false);
      return;
    }

    setIsEditing(false);
    setIsSaving(true);

    try {
      await onSave(editValue);
      setShowSaved(true);
      setTimeout(() => setShowSaved(false), 2000);
    } catch (error) {
      console.error('Error saving:', error);
    } finally {
      setIsSaving(false);
    }
  }, [editValue, value, onSave]);

  const handleCancel = useCallback(() => {
    setEditValue(value || '');
    setIsEditing(false);
  }, [value]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !multiline) {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  }, [handleSave, handleCancel, multiline]);

  if (isEditing) {
    return (
      <div className={`editable-text editing ${className}`} onClick={(e) => e.stopPropagation()}>
        {multiline ? (
          <textarea
            ref={inputRef}
            className="editable-input multiline"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            rows={3}
          />
        ) : (
          <input
            ref={inputRef}
            type={inputType}
            className="editable-input"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
          />
        )}
        <button className="editable-cancel" onClick={handleCancel}>✕</button>
        <button className="editable-done" onClick={handleSave}>✓</button>
      </div>
    );
  }

  if (isSaving) {
    return (
      <span className={`editable-text saving ${className}`}>
        <span className="editable-saving-text">{editValue}</span>
        <span className="editable-saving-indicator" />
      </span>
    );
  }

  return (
    <span
      className={`editable-text ${className} ${showSaved ? 'just-saved' : ''}`}
      onClick={handleTap}
    >
      {value || <span className="editable-placeholder">{placeholder}</span>}
      {showSaved && <span className="editable-saved-indicator">✓</span>}
    </span>
  );
}

function MobileGroupDetail({
  group,
  donors,
  onSelectPerson,
  onBack,
  onEditGroup,
  onAddPerson,
  onOpenAddPersonModal,
  onCreatePerson,
  onExport,
  savedLists = [],
  onSelectList,
  activeList,
  onBackFromList,
  onCreateList,
  savedTables = [],
  onSelectTable,
  activeTable,
  onBackFromTable,
  onCreateTable,
  onAddTableColumn,
  onUpdateGroup,
  onUpdateMember,
  onUpdateTable,
  onUpdateList,
  highlightedPerson,
  onClearHighlight
}) {
  const [showAddPerson, setShowAddPerson] = useState(false);
  const highlightedRef = useRef(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [addingPerson, setAddingPerson] = useState(null);
  const [showMenu, setShowMenu] = useState(false);
  const [contextExpanded, setContextExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState('members'); // 'members', 'lists', or 'tables'
  const [listPrompt, setListPrompt] = useState('');
  const [isCreatingList, setIsCreatingList] = useState(false);
  const [showCreateList, setShowCreateList] = useState(false);
  const [tablePrompt, setTablePrompt] = useState('');
  const [isCreatingTable, setIsCreatingTable] = useState(false);
  const [showCreateTable, setShowCreateTable] = useState(false);
  const [columnPrompt, setColumnPrompt] = useState('');
  const [isAddingColumn, setIsAddingColumn] = useState(false);
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [quickAddName, setQuickAddName] = useState('');
  const [isCreatingPerson, setIsCreatingPerson] = useState(false);

  // Get lists for this group (check both groupId and group_id)
  const groupLists = useMemo(() => {
    return savedLists.filter(list =>
      list.groupId === group.id || list.group_id === group.id
    );
  }, [savedLists, group.id]);

  // Get tables for this group
  const groupTables = useMemo(() => {
    return savedTables.filter(table =>
      table.groupId === group.id || table.group_id === group.id
    );
  }, [savedTables, group.id]);

  // Subgroup colors for list view
  const subgroupColors = ['#4ade80', '#fbbf24', '#fb923c', '#f87171', '#a78bfa', '#38bdf8', '#2dd4bf'];

  // Get members of this group
  const members = useMemo(() => {
    const donorList = Object.entries(donors || {}).map(([name, data]) => ({
      name,
      ...data
    }));

    return donorList.filter(d =>
      d.groupId === group.id || d.group_id === group.id
    ).sort((a, b) => {
      // Sort by amount (highest first), then by name
      const amtA = parseFloat(String(a.amount || '0').replace(/[^0-9.]/g, '')) || 0;
      const amtB = parseFloat(String(b.amount || '0').replace(/[^0-9.]/g, '')) || 0;
      if (amtB !== amtA) return amtB - amtA;
      return a.name.localeCompare(b.name);
    });
  }, [donors, group.id]);

  // Get non-members for add person picker
  const nonMembers = useMemo(() => {
    const donorList = Object.entries(donors || {}).map(([name, data]) => ({
      name,
      ...data
    }));

    return donorList.filter(d =>
      d.groupId !== group.id && d.group_id !== group.id
    ).filter(d => {
      if (!searchQuery.trim()) return true;
      return d.name.toLowerCase().includes(searchQuery.toLowerCase());
    }).sort((a, b) => a.name.localeCompare(b.name));
  }, [donors, group.id, searchQuery]);

  // Calculate total amount
  const totalAmount = useMemo(() => {
    return members.reduce((sum, d) => {
      const amt = parseFloat(String(d.amount || '0').replace(/[^0-9.]/g, '')) || 0;
      return sum + amt;
    }, 0);
  }, [members]);

  // Scroll to highlighted person from search
  useEffect(() => {
    if (highlightedPerson && highlightedRef.current) {
      // Scroll into view
      setTimeout(() => {
        highlightedRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 200);
    }
  }, [highlightedPerson]);

  // Clear highlight on any tap
  const handleClearHighlight = useCallback(() => {
    if (highlightedPerson) {
      onClearHighlight?.();
    }
  }, [highlightedPerson, onClearHighlight]);

  // Format amount
  const formatAmount = (amt) => {
    if (amt >= 1000000) return `$${(amt / 1000000).toFixed(1)}M`;
    if (amt >= 1000) return `$${Math.round(amt / 1000)}K`;
    if (amt > 0) return `$${amt}`;
    return null;
  };

  // Generate consistent color for person (cool colors only)
  const getPersonColor = (name) => {
    const colors = ['#3b82f6', '#8b5cf6', '#06b6d4', '#10b981', '#6366f1', '#14b8a6', '#0ea5e9'];
    const index = name.charCodeAt(0) % colors.length;
    return colors[index];
  };

  // Get initials
  const getInitials = (name) => {
    return name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
  };

  // Format status from donor data
  const getStatus = (donor) => {
    // Check for stage
    if (donor.stage && donor.stage !== 'Unknown') {
      return donor.stage;
    }
    // Fallback to last contact
    if (donor.lastContact && donor.lastContact !== 'Unknown') {
      const date = new Date(donor.lastContact);
      if (!isNaN(date.getTime())) {
        const diffDays = Math.floor((new Date() - date) / 86400000);
        if (diffDays === 0) return 'Contacted today';
        if (diffDays === 1) return 'Contacted yesterday';
        if (diffDays < 7) return `${diffDays} days ago`;
        if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
        return `${Math.floor(diffDays / 30)} months ago`;
      }
    }
    return null;
  };

  // Handle export
  const handleExport = () => {
    if (onExport) {
      onExport(group, members);
    } else {
      // Default CSV export
      const headers = ['Name', 'Amount', 'Stage', 'Last Contact', 'Narrative'];
      const csvRows = [headers.join(',')];

      members.forEach(member => {
        const row = [
          `"${(member.name || '').replace(/"/g, '""')}"`,
          `"${(member.amount || '').replace(/"/g, '""')}"`,
          `"${(member.stage || '').replace(/"/g, '""')}"`,
          `"${(member.lastContact || '').replace(/"/g, '""')}"`,
          `"${(member.narrative || '').replace(/"/g, '""').replace(/\n/g, ' ')}"`
        ];
        csvRows.push(row.join(','));
      });

      const csvContent = csvRows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${group.name.replace(/\s+/g, '-').toLowerCase()}-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    }
  };

  // Handle add person
  const handleAddPerson = async (person) => {
    if (!onAddPerson || addingPerson) return;

    setAddingPerson(person.name);
    try {
      await onAddPerson(person, group);
      // Don't close sheet - let them add more
    } catch (error) {
      console.error('Failed to add person:', error);
    } finally {
      setAddingPerson(null);
    }
  };

  // Handle create list
  const handleCreateList = async () => {
    if (!listPrompt.trim() || isCreatingList || !onCreateList) return;

    setIsCreatingList(true);
    try {
      const newList = await onCreateList(listPrompt.trim(), group);
      setListPrompt('');
      setShowCreateList(false);
      // Auto-select the new list to show it
      if (newList && onSelectList) {
        onSelectList(newList);
      }
    } catch (error) {
      console.error('Failed to create list:', error);
    } finally {
      setIsCreatingList(false);
    }
  };

  // Handle create table
  const handleCreateTable = async () => {
    if (!tablePrompt.trim() || isCreatingTable || !onCreateTable) return;

    setIsCreatingTable(true);
    try {
      const newTable = await onCreateTable(tablePrompt.trim(), group, members);
      setTablePrompt('');
      setShowCreateTable(false);
      // Auto-select the new table to show it
      if (newTable && onSelectTable) {
        onSelectTable(newTable);
      }
    } catch (error) {
      console.error('Failed to create table:', error);
    } finally {
      setIsCreatingTable(false);
    }
  };

  // Handle add column to table
  const handleAddColumn = async () => {
    if (!columnPrompt.trim() || isAddingColumn || !onAddTableColumn || !activeTable) return;

    setIsAddingColumn(true);
    try {
      await onAddTableColumn(columnPrompt.trim(), activeTable);
      setColumnPrompt('');
    } catch (error) {
      console.error('Failed to add column:', error);
    } finally {
      setIsAddingColumn(false);
    }
  };

  // Handle quick add - create new person and add to group
  const handleQuickAdd = async () => {
    if (!quickAddName.trim() || isCreatingPerson || !onCreatePerson) return;

    setIsCreatingPerson(true);
    try {
      const newPerson = await onCreatePerson(quickAddName.trim(), group);
      setQuickAddName('');
      setShowQuickAdd(false);
      // Open their profile
      if (newPerson && onSelectPerson) {
        onSelectPerson(newPerson);
      }
    } catch (error) {
      console.error('Failed to create person:', error);
    } finally {
      setIsCreatingPerson(false);
    }
  };

  return (
    <div className="mobile-group-detail" onClick={handleClearHighlight}>
      {/* Header - hidden when viewing a list or table */}
      {!activeList && !activeTable && (
      <div className="group-detail-header">
        <div className="group-detail-title-row">
          <button className="nested-back-btn" onClick={onBack}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#a78bfa" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <h1 className="group-detail-title">
            <EditableText
              value={group.name}
              onSave={(value) => onUpdateGroup && onUpdateGroup(group.id, { name: value })}
              placeholder="Group name"
              className="editable-group-name"
            />
          </h1>
          {totalAmount > 0 && (
            <span className="group-detail-total">{formatAmount(totalAmount)}</span>
          )}
        </div>

        {/* Action chips row */}
        <div className="group-detail-actions">
          <button
            className="group-detail-chip"
            onClick={() => onOpenAddPersonModal ? onOpenAddPersonModal(group.id) : setShowQuickAdd(true)}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Add
          </button>
          <button
            className={`group-detail-chip ${contextExpanded ? 'active' : ''}`}
            onClick={() => setContextExpanded(!contextExpanded)}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="16" x2="12" y2="12" />
              <line x1="12" y1="8" x2="12.01" y2="8" />
            </svg>
            Context
          </button>
        </div>

        {/* Context expanded content */}
        {contextExpanded && (
          <div className="group-detail-context-content">
            <EditableText
              value={group.context || ''}
              onSave={(value) => onUpdateGroup && onUpdateGroup(group.id, { context: value })}
              placeholder="Add context for AI..."
              multiline={true}
              className="editable-context"
            />
          </div>
        )}

        {/* Tabs */}
        <div className="group-detail-tabs">
          <button
            className={`group-detail-tab ${activeTab === 'members' && !activeList && !activeTable ? 'active' : ''}`}
            onClick={() => {
              if (activeList) onBackFromList?.();
              if (activeTable) onBackFromTable?.();
              setActiveTab('members');
            }}
          >
            Members
            <span className="group-detail-tab-count">{members.length}</span>
          </button>
          <button
            className={`group-detail-tab ${activeTab === 'lists' && !activeTable ? 'active' : ''}`}
            onClick={() => {
              if (activeTable) onBackFromTable?.();
              setActiveTab('lists');
            }}
          >
            Lists
            {groupLists.length > 0 && (
              <span className="group-detail-tab-count">{groupLists.length}</span>
            )}
          </button>
          <button
            className={`group-detail-tab ${activeTab === 'tables' && !activeList ? 'active' : ''}`}
            onClick={() => {
              if (activeList) onBackFromList?.();
              setActiveTab('tables');
            }}
          >
            Tables
            {groupTables.length > 0 && (
              <span className="group-detail-tab-count">{groupTables.length}</span>
            )}
          </button>
        </div>

        {/* More menu */}
        {showMenu && (
          <div className="group-detail-menu-overlay" onClick={() => setShowMenu(false)}>
            <div className="group-detail-menu" onClick={(e) => e.stopPropagation()}>
              <button
                className="group-detail-menu-item"
                onClick={() => {
                  setShowMenu(false);
                  onEditGroup && onEditGroup(group);
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
                Edit group
              </button>
              <button
                className="group-detail-menu-item"
                onClick={() => {
                  setShowMenu(false);
                  handleExport();
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                Export CSV
              </button>
            </div>
          </div>
        )}
      </div>
      )}

      {/* Content area */}
      {activeList ? (
        /* Inside List View */
        <div className="group-detail-list-view">
          <div className="list-view-header">
            <button className="nested-back-btn" onClick={onBackFromList}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#a78bfa" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
            <span className="list-view-name">
              <EditableText
                value={activeList.name || activeList.organizationPrompt || ''}
                onSave={(value) => onUpdateList && onUpdateList(activeList.id, { name: value })}
                placeholder="List name"
                className="editable-list-name"
              />
            </span>
          </div>

          <div className="list-view-content">
            {(activeList.groups || []).map((subgroup, idx) => (
              <div key={idx} className="list-subgroup">
                <div className="list-subgroup-header">
                  <div
                    className="list-subgroup-dot"
                    style={{ background: subgroupColors[idx % subgroupColors.length] }}
                  />
                  <span className="list-subgroup-name">{subgroup.name}</span>
                  <span className="list-subgroup-count">({subgroup.people?.length || 0})</span>
                </div>
                <div className="list-subgroup-chips">
                  {(subgroup.people || []).slice(0, 5).map((person, pIdx) => (
                    <button
                      key={pIdx}
                      className="list-person-chip"
                      onClick={() => {
                        const donor = Object.entries(donors || {}).find(([name]) => name === person)?.[1];
                        if (donor) onSelectPerson({ name: person, ...donor });
                      }}
                    >
                      {person}
                    </button>
                  ))}
                  {(subgroup.people?.length || 0) > 5 && (
                    <span className="list-more-chip">+{subgroup.people.length - 5} more</span>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Query input */}
          <div className="list-view-footer">
            <div className="list-query-input">
              <span className="list-query-placeholder">Ask about this list...</span>
              <div className="list-query-btn" />
            </div>
          </div>
        </div>
      ) : activeTable ? (
        /* Inside Table View - Cards with column data */
        <div className="group-detail-table-view">
          <div className="table-view-header">
            <button className="nested-back-btn" onClick={onBackFromTable}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#a78bfa" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
            <span className="table-view-name">
              <EditableText
                value={activeTable.name}
                onSave={(value) => onUpdateTable && onUpdateTable(activeTable.id, { name: value })}
                placeholder="Table name"
                className="editable-table-name"
              />
            </span>
          </div>

          {/* Column chips */}
          <div className="table-column-chips">
            {(activeTable.columns || []).map((col, idx) => (
              <div key={idx} className="table-column-chip">
                {col.name || col}
              </div>
            ))}
            <button
              className="table-column-chip add-column"
              onClick={() => document.querySelector('.table-add-input')?.focus()}
            >
              + Add
            </button>
          </div>

          {/* Cards */}
          <div className="table-cards">
            {(activeTable.rows || []).map((row, idx) => {
              // Handle both "Name" and "name" from API
              const personName = row.Name || row.name;
              const personAmount = row.Amount || row.amount;
              return (
                <div
                  key={idx}
                  className="table-card"
                  onClick={() => {
                    const donor = Object.entries(donors || {}).find(([name]) => name === personName)?.[1];
                    if (donor) onSelectPerson({ name: personName, ...donor });
                  }}
                >
                  {/* Name + Amount row */}
                  <div className="table-card-header">
                    <span className="table-card-name">{personName}</span>
                    {personAmount && (
                      <span className="table-card-amount">{personAmount}</span>
                    )}
                  </div>

                  {/* Data fields */}
                  <div className="table-card-fields">
                    {(activeTable.columns || []).filter(col => {
                      const colName = col.name || col;
                      return colName !== 'Name' && colName !== 'name' && colName !== 'Amount' && colName !== 'amount';
                    }).map((col, colIdx) => {
                      const colName = col.name || col;
                      const value = row[colName] || row[colName.toLowerCase()] || row[colName.charAt(0).toUpperCase() + colName.slice(1)] || '';
                      return (
                        <div key={colIdx} className="table-card-field">
                          <span className="table-card-field-label">{colName}</span>
                          <span className={`table-card-field-value ${colName.toLowerCase().includes('interest') ? 'interest' : ''}`}>
                            <EditableText
                              value={value !== '—' ? value : ''}
                              onSave={(newValue) => onUpdateTable && onUpdateTable(activeTable.id, {
                                rows: activeTable.rows.map((r, i) =>
                                  i === idx ? { ...r, [colName]: newValue } : r
                                )
                              })}
                              placeholder="—"
                              className="editable-table-cell"
                            />
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Add column input */}
          <div className="table-view-footer">
            <div className={`table-add-input-wrapper ${isAddingColumn ? 'loading' : ''}`}>
              <input
                type="text"
                className="table-add-input"
                placeholder="Add a column..."
                value={columnPrompt}
                onChange={(e) => setColumnPrompt(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleAddColumn();
                  }
                }}
                disabled={isAddingColumn}
              />
              <button
                className={`table-add-send ${columnPrompt.trim() ? 'active' : ''}`}
                onClick={handleAddColumn}
                disabled={!columnPrompt.trim() || isAddingColumn}
              >
                {isAddingColumn ? '...' : '↑'}
              </button>
            </div>
          </div>
        </div>
      ) : activeTab === 'tables' ? (
        /* All Tables View */
        <div className="group-detail-tables">
          {groupTables.length === 0 ? (
            <div className="group-detail-empty">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <line x1="3" y1="9" x2="21" y2="9" />
                <line x1="9" y1="21" x2="9" y2="9" />
              </svg>
              <p>No tables yet</p>
            </div>
          ) : (
            groupTables.map(table => (
              <button
                key={table.id}
                className="group-table-row"
                onClick={() => onSelectTable && onSelectTable(table)}
              >
                <div className="group-table-info">
                  <div className="group-table-name">{table.name}</div>
                  <div className="group-table-meta">
                    {table.columns?.length || 0} columns · {table.rows?.length || 0} people
                  </div>
                </div>
                <span className="group-table-arrow">›</span>
              </button>
            ))
          )}

          {/* Create new table button */}
          <button
            className="group-table-create"
            onClick={() => setShowCreateTable(true)}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Create new table
          </button>
        </div>
      ) : activeTab === 'lists' ? (
        /* All Lists View */
        <div className="group-detail-lists">
          {groupLists.length === 0 ? (
            <div className="group-detail-empty">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <line x1="8" y1="6" x2="21" y2="6" />
                <line x1="8" y1="12" x2="21" y2="12" />
                <line x1="8" y1="18" x2="21" y2="18" />
                <line x1="3" y1="6" x2="3.01" y2="6" />
                <line x1="3" y1="12" x2="3.01" y2="12" />
                <line x1="3" y1="18" x2="3.01" y2="18" />
              </svg>
              <p>No lists yet</p>
            </div>
          ) : (
            groupLists.map(list => (
              <button
                key={list.id}
                className="group-list-row"
                onClick={() => onSelectList && onSelectList(list)}
              >
                <div className="group-list-info">
                  <div className="group-list-name">{list.name || list.organizationPrompt}</div>
                  <div className="group-list-meta">
                    {list.groups?.length || 0} groups · {list.groups?.reduce((sum, g) => sum + (g.people?.length || 0), 0) || 0} people
                  </div>
                </div>
                <span className="group-list-arrow">›</span>
              </button>
            ))
          )}

          {/* Create new list button */}
          <button
            className="group-list-create"
            onClick={() => setShowCreateList(true)}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Create new list
          </button>
        </div>
      ) : (
        /* Members View */
        <div className="group-detail-members">
          {members.length === 0 ? (
            <div className="group-detail-empty">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
              <p>No people in this group yet</p>
            </div>
          ) : (
            members.map(member => (
              <button
                key={member.name}
                ref={highlightedPerson === member.name ? highlightedRef : null}
                className={`group-detail-row ${highlightedPerson === member.name ? 'highlighted' : ''}`}
                onClick={() => onSelectPerson(member)}
              >
                <div
                  className="group-detail-avatar"
                  style={{ borderColor: getPersonColor(member.name), color: getPersonColor(member.name) }}
                >
                  {getInitials(member.name)}
                </div>
                <div className="group-detail-info">
                  <div className="group-detail-name">{member.name}</div>
                  {getStatus(member) && (
                    <div className="group-detail-status">{getStatus(member)}</div>
                  )}
                </div>
                <div className="group-detail-right">
                  <div className="group-detail-amount">
                    <EditableText
                      value={member.amount && member.amount !== 'Unknown' ? member.amount : ''}
                      onSave={(value) => onUpdateMember && onUpdateMember(member.name, 'amount', value)}
                      placeholder="$0"
                      className="editable-member-amount"
                    />
                  </div>
                  <span className="group-detail-arrow">›</span>
                </div>
              </button>
            ))
          )}
        </div>
      )}

      {/* Footer - only show for members tab */}
      {activeTab === 'members' && !activeList && (
        <div className="group-detail-footer">
          <button
            className="group-detail-add-btn"
            onClick={() => onOpenAddPersonModal ? onOpenAddPersonModal(group.id) : setShowAddPerson(true)}
          >
            Add Person to Group
          </button>
        </div>
      )}

      {/* Add person sheet */}
      {showAddPerson && (
        <div className="group-add-overlay" onClick={() => setShowAddPerson(false)}>
          <div className="group-add-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="group-add-handle" />

            <h2 className="group-add-title">Add to {group.name}</h2>

            <div className="group-add-search">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                type="text"
                placeholder="Search people..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                
              />
            </div>

            <div className="group-add-list">
              {nonMembers.length === 0 ? (
                <div className="group-add-empty">
                  {searchQuery ? 'No matches found' : 'Everyone is already in this group'}
                </div>
              ) : (
                nonMembers.slice(0, 20).map(person => (
                  <button
                    key={person.name}
                    className="group-add-row"
                    onClick={() => handleAddPerson(person)}
                    disabled={addingPerson === person.name}
                  >
                    <div
                      className="group-add-avatar"
                      style={{ borderColor: getPersonColor(person.name), color: getPersonColor(person.name) }}
                    >
                      {getInitials(person.name)}
                    </div>
                    <span className="group-add-name">{person.name}</span>
                    {addingPerson === person.name ? (
                      <span className="group-add-spinner" />
                    ) : (
                      <svg className="group-add-plus" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="12" y1="5" x2="12" y2="19" />
                        <line x1="5" y1="12" x2="19" y2="12" />
                      </svg>
                    )}
                  </button>
                ))
              )}
            </div>

            <button
              className="group-add-done"
              onClick={() => {
                setShowAddPerson(false);
                setSearchQuery('');
              }}
            >
              Done
            </button>
          </div>
        </div>
      )}

      {/* Create list sheet */}
      {showCreateList && (
        <div className="group-add-overlay" onClick={() => !isCreatingList && setShowCreateList(false)}>
          <div className="group-add-sheet create-list-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="group-add-handle" />

            <h2 className="group-add-title">Create List</h2>

            <p className="create-list-hint">
              How should we organize the {members.length} people?
            </p>

            <div className="create-list-input-wrapper">
              <input
                type="text"
                className="create-list-input-field"
                placeholder="By giving likelihood..."
                value={listPrompt}
                onChange={(e) => setListPrompt(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleCreateList();
                  }
                }}
                disabled={isCreatingList}
                
              />
            </div>

            <div className="create-list-actions">
              <button
                className="group-add-done"
                onClick={() => {
                  setShowCreateList(false);
                  setListPrompt('');
                }}
                disabled={isCreatingList}
              >
                Cancel
              </button>
              <button
                className="create-list-submit"
                onClick={handleCreateList}
                disabled={!listPrompt.trim() || isCreatingList}
              >
                {isCreatingList ? 'Creating...' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create table sheet */}
      {showCreateTable && (
        <div className="group-add-overlay" onClick={() => !isCreatingTable && setShowCreateTable(false)}>
          <div className="group-add-sheet create-table-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="group-add-handle" />

            <h2 className="group-add-title">Create Table</h2>

            <p className="create-table-hint">
              What columns should we extract for these {members.length} people?
            </p>

            <div className="create-table-examples">
              <span className="create-table-example">Role</span>
              <span className="create-table-example">Term Ends</span>
              <span className="create-table-example">Giving</span>
              <span className="create-table-example">Interest</span>
            </div>

            <div className="create-table-input-wrapper">
              <input
                type="text"
                className="create-table-input-field"
                placeholder="Their role, term end date, and giving..."
                value={tablePrompt}
                onChange={(e) => setTablePrompt(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleCreateTable();
                  }
                }}
                disabled={isCreatingTable}
                
              />
            </div>

            <div className="create-table-actions">
              <button
                className="group-add-done"
                onClick={() => {
                  setShowCreateTable(false);
                  setTablePrompt('');
                }}
                disabled={isCreatingTable}
              >
                Cancel
              </button>
              <button
                className="create-table-submit"
                onClick={handleCreateTable}
                disabled={!tablePrompt.trim() || isCreatingTable}
              >
                {isCreatingTable ? 'Creating...' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quick add person sheet */}
      {showQuickAdd && (
        <div className="group-add-overlay" onClick={() => !isCreatingPerson && setShowQuickAdd(false)}>
          <div className="group-add-sheet quick-add-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="group-add-handle" />

            <h2 className="group-add-title">Add New Person</h2>

            <div className="quick-add-input-wrapper">
              <input
                type="text"
                className="quick-add-input"
                placeholder="Enter name..."
                value={quickAddName}
                onChange={(e) => setQuickAddName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleQuickAdd();
                  }
                }}
                disabled={isCreatingPerson}
                
              />
              <button
                className={`quick-add-submit ${quickAddName.trim() ? 'active' : ''}`}
                onClick={handleQuickAdd}
                disabled={!quickAddName.trim() || isCreatingPerson}
              >
                {isCreatingPerson ? (
                  <span className="quick-add-spinner" />
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="5" y1="12" x2="19" y2="12" />
                    <polyline points="12 5 19 12 12 19" />
                  </svg>
                )}
              </button>
            </div>

            <p className="quick-add-hint">
              Will be added to {group.name}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default MobileGroupDetail;
